import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { DateTime } from "luxon";
import { useSwipeable } from "react-swipeable";

import { format } from "date-fns";

import Loader from "../../Components/Loader";
import CustomButton from "../../Components/CustomButton";
import ModalConfirmation from "../../Components/ModalConfirmation/ModalConfirmation";
import ScrollableItems from "../../Components/ScrollableItems";
import DotsMenu from "../../Components/DotsMenu";

import { monthNames } from "../../store/GlobalVarriables";
import useApi from "../../hooks/useApi";
import { useStatusModal } from "../../context/StatusModalContext";

import { getNext30Days } from "../../helpers/days";
import { getDecodedToken } from "../../utils/helpers";

import { ReactComponent as ExclamationIcon } from "../../static/images/exclamation_sign.svg";

import "./style.scss";

const ScheduleProvider = () => {
  const { get, patch, post, loading } = useApi();
  const { t } = useTranslation();

  const [confirmModalText, setConfirmModalText] = useState("");
  const [confirmModalToggle, setConfirmModalToggle] = useState("");
  const [selectedItem, setSelectedItem] = useState(null);

  const { showStatusModal } = useStatusModal();
  const [activeDay, setActiveDay] = useState(0);
  const [days, setDays] = useState(getNext30Days());
  const navigate = useNavigate();
  const eventsRefs = useRef([]);

  const [scheduleEvents, setScheduleEvents] = useState([]);

  const [dayKey, setDayKey] = useState();
  const [currentTime, setCurrentTime] = useState();

  const menuItems = [
    {
      text: t("session_details"),
      onClick: () => {
        detailsClick();
      },
    },

    {
      text: t("cancel_session"),
      onClick: () => {
        setConfirmModalToggle("modal-confirm");
        setConfirmModalText({
          modalText: t("cancel_session_confirmation_modal_text", {
            date: displayDataFormat(selectedItem?.eventDate),
          }),
          modalSubText: t("cancel_session_confirmation_modal_subText"),
          confirm: () => cancelSession(),
        });
      },
    },
    {
      text: t("reschedule"),
      onClick: () => {
        reschduleClick();
      },
    },
  ];

  const handleToggleMenu = (item) => {
    setSelectedItem((prevEvent) =>
      prevEvent?.genId === item.genId ? null : item,
    );
  };

  const handleSelectDay = (index, day, hasEvents) => {
    setActiveDay(index);
  };

  const fetchRequests = async () => {
    try {
      const data = await get(
        `/Providers/Request?ProviderId=${getDecodedToken().buid}&Statuses=pending`,
      );

      if (data.length) {
        setConfirmModalToggle("modal-confirm");
        setConfirmModalText({
          modalText: t("is_request_message"),
          modalSubText: "",
          confirmButtonText: t("see_requests"),
          confirm: () => navigate("/requests"),
        });
      }
    } catch (err) {
      console.log(err);
    }
  };

  const getScheduleEvents = async () => {
    const timezone = DateTime.now().zoneName;
    try {
      const data = await post(`/UpcomingEvents/Provider`, {
        providerId: getDecodedToken().buid,
        timezone: timezone,
      });
      formatScheduleData(data);
    } catch (error) {
      console.error(error);
    }
  };

  const formatScheduleData = (scheduleData) => {
    if (!scheduleData.length) {
      return;
    }

    const dateMap = {};

    const formatDateKey = (date) => {
      const day = String(date.getDate()).padStart(2, "0");
      const month = date
        .toLocaleString("en-GB", { month: "short" })
        .toLowerCase();
      return `${day} ${month}`;
    };

    const formatHourKey = (date) => {
      return `${String(date.getHours()).padStart(2, "0")}:00`;
    };

    const getWidthIndex = (index, length) => {
      const indexValue = index + 1;

      if (indexValue === 1) {
        return "0%";
      }
      if (length === 2) {
        if (indexValue === 2) {
          return `${100 / 2}%`;
        }
      } else if (length === 3) {
        if (indexValue === 2) {
          return `${100 / 3}%`;
        } else if (indexValue === 3) {
          return `${(100 / 3) * 2}%`;
        }
      } else if (length === 4) {
        if (indexValue === 2) {
          return `25%`;
        } else if (indexValue === 3) {
          return `50%`;
        } else if (indexValue === 4) {
          return `75%`;
        }
      } else if (length === 5) {
        if (indexValue === 2) {
          return `20%`;
        } else if (indexValue === 3) {
          return `40%`;
        } else if (indexValue === 4) {
          return `60%`;
        } else if (indexValue === 5) {
          return `80%`;
        }
      }
    };

    const getHour = (date) => new Date(date).getHours();

    const getHeight = (duration) => {
      if (duration <= 30) {
        return 0.5;
      } else {
        return `${duration / 30 / 2}`;
      }
    };

    const getMinute = (date) => {
      const min = new Date(date).getMinutes();
      return min * (5 / 6);
    };

    function generateUniqueId() {
      const timestamp = new Date().getTime();
      const random = Math.random().toString(36).substring(2, 8);
      return `${timestamp}${random}`;
    }

    for (const sched of scheduleData) {
      for (const event of sched.events) {
        const curretnDate = new Date();
        const lastDate = new Date(event.eventEndDate);
        const eventDate = new Date(event.eventDate);
        const dateKey = formatDateKey(eventDate);
        const hourKey = formatHourKey(eventDate);
        const hour = getHour(eventDate);
        const startHour = getHour(eventDate);
        const endHour = getHour(event.eventEndDate);
        const minute = getMinute(eventDate);
        const weedDay = new Date(eventDate).toLocaleString("en-US", {
          weekday: "long",
        });

        const originalEventDate = event.originalEventDate
          ? new Date(event.originalEventDate)
          : eventDate;

        if (!dateMap[dateKey]) {
          dateMap[dateKey] = {
            hours: {},
            allHours: [],
          };
        }

        if (
          curretnDate.toDateString() >= eventDate.toDateString() &&
          curretnDate.toDateString() <= lastDate.toDateString()
        ) {
          let currentTimeData = {
            position: getMinute(curretnDate),
            hour: formatHourKey(curretnDate),
            day: formatDateKey(curretnDate),
          };
          setCurrentTime(currentTimeData);
        }

        const enrichedEvent = {
          // ...event,
          scheduleId: sched.scheduelId,
          description: sched.description,
          duration: sched.duration,
          maxMembers: sched.maxMembers,
          currentMembers: sched.currentMembers,
          isOnetime: sched.isOnetime,
          groupSchedule: sched.groupSchedule,
          hasSubscriptions: sched.hasSubscriptions,
          Status: "Scheduled",

          eventDate: eventDate,
          eventEndDate: event.eventEndDate,
          eventId: event.eventId,
          originalEventDate: originalEventDate,
          scheduleDayId: event.scheduleDayId,

          weedDay: weedDay,
          genId: generateUniqueId(),
          heightIndex: getHeight(sched.duration),
          position: `${minute}px`,
        };

        if (!dateMap[dateKey].hours[hourKey]) {
          dateMap[dateKey].hours[hourKey] = [];
        }

        dateMap[dateKey].hours[hourKey].push(enrichedEvent);
        dateMap[dateKey].allHours.push(startHour, endHour);
        // dateMap[dateKey].allHours.push(hour);
      }
    }

    // Fill in blanks based only on eventDate hours
    const result = {};

    const sortedDateKeys = Object.keys(dateMap).sort((a, b) => {
      const parse = (k) => new Date(`${k} 2025`);
      return parse(a) - parse(b);
    });

    for (const dateKey of sortedDateKeys) {
      const { hours, allHours } = dateMap[dateKey];
      const minHour = Math.min(...allHours);
      const maxHour = Math.max(...allHours);

      const filledHours = {};

      for (let h = minHour; h <= maxHour; h++) {
        const hourKey = `${String(h).padStart(2, "0")}:00`;
        filledHours[hourKey] =
          hours[hourKey]?.map((item, index, array) => ({
            ...item,
            widthIndex: getWidthIndex(index, array.length),
          })) || "blank";
      }

      result[dateKey] = filledHours;
    }

    let cards = Object.entries(result).flatMap(([date, hours_data]) =>
      Object.entries(hours_data).flatMap(([hour, card]) =>
        Array.isArray(card) ? card : [],
      ),
    );

    const days = getNext30Days(cards);

    const activeDayIndex = days.findIndex((e) => e.hasEvents);

    setActiveDay(activeDayIndex);
    setDays(days);

    setScheduleEvents(result);
  };

  const detailsClick = (itemArg) => {
    const item = itemArg || selectedItem;

    const eventDate = new Date(item.eventDate);
    const originalEventDate = new Date(item.originalEventDate);

    navigate(`/session/${item?.scheduleId}`, {
      state: {
        eventDate: eventDate,
        originalEventDate: originalEventDate,

        scheduleDayId: item.scheduleDayId,
        eventId: item.eventId,
      },
    });
  };

  const reschduleClick = () => {
    const eventDate = new Date(selectedItem.eventDate);
    const originalEventDate = new Date(selectedItem.originalEventDate);

    navigate(`/reschedule/${selectedItem?.scheduleId}`, {
      state: {
        eventDate: eventDate,
        originalEventDate: originalEventDate,

        scheduleDayId: selectedItem.scheduleDayId,
        eventId: selectedItem.eventId,
      },
    });
  };

  const cancelSession = async () => {
    try {
      const method = selectedItem.eventId ? patch : post;
      const eventTime = selectedItem.eventDate;
      const originalEventTime = selectedItem.originalEventDate;

      const dataToPass = selectedItem.eventId
        ? {
            id: selectedItem.eventId,
            eventStatus: "Cancelled",
          }
        : {
            scheduleDayId: selectedItem.scheduleDayId,
            eventTime: eventTime.toISOString(),
            originalEventTime: originalEventTime.toISOString(),
            eventStatus: "Cancelled",
            scheduleId: selectedItem.scheduleId,
          };

      const data = await method("/ApplicationEvent", dataToPass);
      if (data) {
        setSelectedItem(null);
        const successMessage = t("consumer_schedules_cancel_success");
        showStatusModal(successMessage);
        getScheduleEvents();
      }
    } catch (err) {
      console.error(err);
    }
  };

  const displayDataFormat = (date) => {
    if (date) {
      const day = date.getDate();
      const month = monthNames[date.getMonth()];

      const year = date.getFullYear();

      const formattedDate = `${day} of ${month} ${year}`;

      return formattedDate;
    }
  };

  const scrollItems = days.map((item) => ({
    ...item,
    subText: t(item.dayName).slice(0, 3),
  }));

  useEffect(() => {
    getScheduleEvents();
    fetchRequests();
  }, []);

  const dayEntry = Object.entries(scheduleEvents).filter(
    ([date]) => date === dayKey,
  );

  const handlers = useSwipeable({
    onSwipedLeft: () => setActiveDay((prev) => (prev < 30 ? prev + 1 : prev)),
    onSwipedRight: () => setActiveDay((prev) => (prev > 0 ? prev - 1 : prev)),
  });

  return (
    <>
      <ScrollableItems
        items={scrollItems}
        activeItem={activeDay}
        selectItem={handleSelectDay}
        selectDayKey={setDayKey}
      />
      <div className="sessions-container" {...handlers}>
        {scheduleEvents.length === 0 ? (
          <div className="empty-schedule">
            <h1>{t("provider_sessions_empty")}</h1>

            <CustomButton
              fullWidth
              onClick={() => {
                navigate("/invite");
              }}
            >
              {t("create_new_chedule")}
            </CustomButton>
          </div>
        ) : dayEntry.length === 0 ? (
          <>
            <h1 className="empty-sessions">{t("empty_sessions")}</h1>
            <div className="addBtn-container">
              <CustomButton
                fullWidth
                onClick={() => {
                  navigate("/invite");
                }}
              >
                {t("create_new_chedule")}
              </CustomButton>
            </div>
          </>
        ) : (
          <>
            {dayEntry.map(([date, hours_data]) => {
              return (
                <div key={date} className="sessions-date-container">
                  <div className="cards-container">
                    <ul>
                      {Object.entries(hours_data).map(
                        ([hours, card], index) => {
                          return (
                            <li key={index}>
                              <div className="hour">
                                <span>{hours}</span>
                              </div>
                              {currentTime &&
                              currentTime.day === date &&
                              currentTime.hour === hours ? (
                                <div
                                  style={{
                                    top: `${currentTime.position}px`,
                                  }}
                                  id="current-time-container"
                                >
                                  <div id="current-time-dot"></div>
                                  <div id="current-time-line"></div>
                                </div>
                              ) : null}

                              {card && card === "blank" ? (
                                <div className="blanck"></div>
                              ) : (
                                <>
                                  {card && (
                                    <div className="events-container">
                                      {card?.map((item) => {
                                        return (
                                          <div
                                            className={`event ${
                                              selectedItem?.genId === item.genId
                                                ? "index-raised"
                                                : ""
                                            }`}
                                            key={item.genId}
                                            style={{
                                              height: `calc(50px * ${item.heightIndex} - 2px)`,
                                              width: `calc(100% / ${card.length})`,
                                              position: "absolute",
                                              top: `${item.position}`,
                                              left: `${item.widthIndex}`,
                                              flexDirection:
                                                card.length === 3
                                                  ? "column"
                                                  : "",
                                            }}
                                            onClick={() => {
                                              detailsClick(item);
                                            }}
                                            ref={(el) =>
                                              (eventsRefs.current[
                                                format(
                                                  card[0].eventDate,
                                                  "EEE dd",
                                                )
                                              ] = el)
                                            }
                                          >
                                            <span className="session-description">
                                              {item?.description}
                                            </span>

                                            <div className="session-info">
                                              {card.length < 4 ? (
                                                <div
                                                  className="session-members"
                                                  style={{
                                                    marginRight:
                                                      card.length === 3
                                                        ? "0"
                                                        : "15px",
                                                  }}
                                                >
                                                  {item.isOnetime ? (
                                                    <span>
                                                      {item.currentMembers}
                                                    </span>
                                                  ) : (
                                                    <>
                                                      <span>
                                                        {item.currentMembers}
                                                      </span>
                                                      <span>
                                                        /{item.maxMembers}
                                                      </span>
                                                    </>
                                                  )}
                                                </div>
                                              ) : null}

                                              {card.length === 1 ? (
                                                <div
                                                  className="event-menu"
                                                  onClick={(event) => {
                                                    event.stopPropagation();
                                                  }}
                                                >
                                                  <DotsMenu
                                                    menuItems={menuItems}
                                                    isMenuOpen={
                                                      selectedItem?.genId ===
                                                        item.genId &&
                                                      !confirmModalToggle
                                                    }
                                                    toggleMenu={() =>
                                                      handleToggleMenu(item)
                                                    }
                                                  />
                                                </div>
                                              ) : null}
                                            </div>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  )}
                                </>
                              )}
                            </li>
                          );
                        },
                      )}
                    </ul>
                  </div>
                </div>
              );
            })}
          </>
        )}

        <ModalConfirmation
          modalText={confirmModalText.modalText}
          modalSubText={confirmModalText.modalSubText}
          changeModal={setConfirmModalToggle}
          modalValue={confirmModalToggle}
          confirmButtonText={confirmModalText.confirmButtonText}
          confirm={confirmModalText.confirm}
        />

        {loading && <Loader />}
      </div>
    </>
  );
};

export default React.memo(ScheduleProvider);
